import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, BellOff, AlertCircle } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import {
  getCurrentSubscription,
  getPermission,
  isPushSupported,
  refreshSubscriptionIfNeeded,
  subscribeToPush,
  unsubscribeFromPush,
} from "@v2/lib/push";

const VAPID_PUBLIC = (import.meta.env.VITE_WEB_PUSH_VAPID_PUBLIC_KEY as string | undefined) || "";

export function PushSubscribeManager() {
  const { t } = useTranslation("settings");
  const [supported] = useState(isPushSupported());
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(getPermission());
  const [subscribed, setSubscribed] = useState<boolean>(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supported) return;
    getCurrentSubscription().then((sub) => setSubscribed(!!sub));
    refreshSubscriptionIfNeeded().catch(() => {});
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await subscribeToPush();
      if (result.status === "granted" && result.subscription) {
        setSubscribed(true);
        setPermission("granted");
      } else if (result.status === "denied") {
        setPermission("denied");
        setError(t("push.permissionDenied"));
      } else if (result.status === "no-key") {
        setError(t("push.missingKey"));
      } else if (result.status === "unsupported") {
        setError(t("push.browserUnsupported"));
      } else {
        setError(t("push.notGranted"));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t("push.subscribeError");
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : t("push.unsubscribeError");
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BellOff className="h-4 w-4" />
            {t("push.title")}
          </CardTitle>
          <CardDescription>{t("push.unsupported")}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Bell className="h-4 w-4" />
          {t("push.title")}
        </CardTitle>
        <CardDescription>
          {t("push.cardDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-xs text-muted-foreground">
          {t("push.status")}:&nbsp;
          {subscribed ? (
            <span className="text-emerald-600 font-medium">{t("push.statusActive")}</span>
          ) : permission === "denied" ? (
            <span className="text-rose-600 font-medium">{t("push.statusDenied")}</span>
          ) : (
            <span className="text-muted-foreground font-medium">{t("push.statusInactive")}</span>
          )}
        </div>
        {error && (
          <div className="flex items-start gap-2 text-xs text-rose-600 bg-rose-50 px-3 py-2 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        {!VAPID_PUBLIC && (
          <div className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-md">
            {t("push.missingKeyHint")}
          </div>
        )}
        <div className="flex gap-2">
          {!subscribed ? (
            <Button onClick={enable} disabled={busy || !VAPID_PUBLIC} className="gap-2">
              <Bell className="h-4 w-4" />
              {busy ? t("push.enabling") : t("push.enable")}
            </Button>
          ) : (
            <Button variant="outline" onClick={disable} disabled={busy} className="gap-2">
              <BellOff className="h-4 w-4" />
              {busy ? t("push.disabling") : t("push.disable")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
